import type { QueryKey, UseMutationOptions, UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { ALL_CAMPAIGNS_QUERY_PREFIX } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerInvalidation } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import {
    createCampaignQueryKey,
    createRankTrackerQueryKey,
} from "app/pages/digital-marketing/keywords-campaigns/shared/utils";

type OptimisticUpdateContext<TCacheData> = {
    previousQueriesData: [readonly unknown[], TCacheData][];
};

export type RankTrackerMutationOptions<
    TData = unknown,
    TError = Error,
    TVariables = unknown,
    TCacheData = TData,
    TContext = unknown,
> = UseMutationOptions<TData, TError, TVariables, TContext> & {
    queryType: RankTrackerQueryType;
    campaignId?: string;
    optimisticUpdate?: (variables: TVariables) => (data: TCacheData) => TCacheData;
};

type CampaignMutationResult<TData, TError, TVariables, TContext> = UseMutationResult<
    TData,
    TError,
    TVariables,
    TContext
>;

/**
 * Custom useMutation hook for campaign-related queries with built-in query
 * cancellation, optimistic updates, error handling, and cache invalidation.
 *
 * @example
 * // Campaign-specific mutation with optimistic update
 * const { mutate } = useRankTrackerMutation({
 *   campaign: currentCampaignConfiguration,
 *   queryType: RankTrackerQueryType.KeywordsTable,
 *   mutationFn: (config) => updateKeywordsFromCampaign(config, campaign.id),
 *   optimisticUpdate: (config) => (data) => updateTagsInTableData(data, config.updatedTags),
 *   onError: (error, variables, context) => {
 *     // Optimistic update is automatically reverted on error
 *     showErrorToast("Error");
 *   },
 *   onSuccess: async () => {
 *     // removeCampaignQueries is called automatically with invalidateOptions (if provided)
 *     showSuccessToast("Success");
 *   },
 * });
 *
 * @example
 * // All campaigns query mutation with optimistic update
 * const { mutate } = useRankTrackerMutation({
 *   campaign: ALL_CAMPAIGNS_QUERY_PREFIX,
 *   queryType: RankTrackerQueryType.ListCampaigns,
 *   mutationFn: (newCampaign) => createCampaign(newCampaign),
 *   optimisticUpdate: (newCampaign) => (campaignsList) => [...campaignsList, newCampaign],
 *   onError: (error, variables, context) => {
 *     // Optimistic update is automatically reverted on error
 *     showErrorToast("Error");
 *   },
 *   onSuccess: async () => {
 *     // removeCampaignQueries is called automatically with invalidateOptions (if provided)
 *     showSuccessToast("Success");
 *   },
 * });
 *
 * @example
 * // Other query mutation not tied to a campaign.
 * const { mutate } = useRankTrackerMutation({
 *   queryType: RankTrackerQueryType.Languages,
 *   mutationFn: (newLanguage) => createLanguage(newLanguage),
 *   optimisticUpdate: (newLanguage) => (languageList) => [...languageList, newLanguage],
 *   onError: (error, variables, context) => {
 *     // Optimistic update is automatically reverted on error
 *     showErrorToast("Error");
 *   },
 *   onSuccess: async () => {
 *     // removeCampaignQueries is called automatically with invalidateOptions (if provided)
 *     showSuccessToast("Success");
 *   },
 * });
 */
export const useRankTrackerMutation = <
    TData = unknown,
    TError = Error,
    TVariables = unknown,
    TCacheData = TData,
    TContext = unknown,
>({
    campaignId,
    queryType,
    mutationFn,
    optimisticUpdate,
    onMutate: customOnMutate,
    onError: customOnError,
    onSuccess: customOnSuccess,
    ...restOptions
}: RankTrackerMutationOptions<TData, TError, TVariables, TCacheData, TContext>): CampaignMutationResult<
    TData,
    TError,
    TVariables,
    TContext & OptimisticUpdateContext<TCacheData>
> => {
    const queryClient = useQueryClient();
    const { removeCampaignQueries } = useRankTrackerInvalidation();

    const getQueryKeyPrefix = (): QueryKey | undefined => {
        if (!campaignId) {
            return createRankTrackerQueryKey(queryType);
        }

        return createCampaignQueryKey(campaignId, queryType);
    };

    type Context = TContext & OptimisticUpdateContext<TCacheData>;

    return useMutation<TData, TError, TVariables, Context>({
        mutationFn,
        onMutate: async (variables) => {
            const prefix = getQueryKeyPrefix();

            if (!prefix) {
                throw new Error("onMutate called without prefix, ensure that campaign is set.");
            }

            await queryClient.cancelQueries({ queryKey: prefix });
            const baseContext = customOnMutate ? await customOnMutate(variables) : undefined;
            const previousQueriesData: [QueryKey, TCacheData][] = [];

            if (optimisticUpdate) {
                const updater = optimisticUpdate(variables);
                const existingData = queryClient.getQueriesData<TCacheData>({ queryKey: prefix });

                existingData.forEach(([queryKey, data]) => {
                    if (data !== undefined) {
                        previousQueriesData.push([queryKey, data]);
                        const updatedData = updater(data);
                        queryClient.setQueryData(queryKey, updatedData);
                    }
                });
            }

            return {
                ...baseContext,
                previousQueriesData,
            } as Context;
        },
        onError: async (error, variables, context) => {
            if (context?.previousQueriesData.length) {
                context.previousQueriesData.forEach(([queryKey, previousData]) => {
                    queryClient.setQueryData(queryKey, previousData);
                });
            }

            if (customOnError) {
                await customOnError(error, variables, context);
            }
        },
        onSuccess: async (data, variables, context) => {
            if (campaignId && campaignId !== ALL_CAMPAIGNS_QUERY_PREFIX) {
                await removeCampaignQueries({
                    campaignId,
                    // We keep the optimistically updated query type.
                    queryTypesToKeep: optimisticUpdate ? [queryType] : [],
                });
            }

            if (customOnSuccess) {
                await customOnSuccess(data, variables, context);
            }
        },
        ...restOptions,
    });
};
